/**
 * Shared helper functions for browser tool results.
 *
 * Used by both the API adapter and CLI MCP tools.
 *
 * @module
 */

import type {
  ContextRoute,
  ContextRouteHandle,
  IBrowserClient,
  MCPContentResult,
  RecoveryInfo,
} from '../browser-client.interface.js';
import type { BrowserLogger } from '../logger.js';

export function getMCPContentMedia(result: MCPContentResult): NonNullable<MCPContentResult['media']> {
  return result.media ?? result.images ?? [];
}

/**
 * Check if a result is an MCPContentResult with media.
 */
export function isMCPContentWithMedia(result: unknown): result is MCPContentResult {
  return (
    typeof result === 'object' &&
    result !== null &&
    'text' in result &&
    (Array.isArray((result as MCPContentResult).media) ||
      Array.isArray((result as MCPContentResult).images)) &&
    getMCPContentMedia(result as MCPContentResult).length > 0
  );
}

/**
 * @deprecated Use `isMCPContentWithMedia`.
 */
export function isMCPContentWithImages(result: unknown): result is MCPContentResult {
  return isMCPContentWithMedia(result);
}

/**
 * Extract text content from a string or MCPContentResult.
 */
export function coerceResultText(result: unknown): string {
  if (typeof result === 'string') return result;
  if (typeof result === 'number' || typeof result === 'boolean') return String(result);
  if (Array.isArray(result)) return result.map((item) => coerceResultText(item)).join('\n');
  if (result && typeof result === 'object') {
    // MCPContentResult
    if ('text' in result && typeof (result as MCPContentResult).text === 'string') {
      return (result as MCPContentResult).text;
    }
    try {
      return JSON.stringify(result, null, 2);
    } catch {
      return String(result);
    }
  }
  return '';
}

/**
 * Prepend recovery notice to a text tool result if a crash recovery occurred.
 * Consumes the recovery info so it's only shown once.
 */
export function withRecoveryNotice(
  client: IBrowserClient,
  result: string,
  logger?: BrowserLogger
): string {
  const recovery = client.consumeRecoveryInfo?.();
  if (!recovery) return result;

  const notice = formatRecoveryNotice(recovery, logger);
  return notice + result;
}

/**
 * Apply recovery notice to MCPContentResult (for multimodal results like snapshots).
 */
export function withRecoveryNoticeMCP(
  client: IBrowserClient,
  result: string | MCPContentResult,
  logger?: BrowserLogger
): string | MCPContentResult {
  const recovery = client.consumeRecoveryInfo?.();
  if (!recovery) return result;

  const notice = formatRecoveryNotice(recovery, logger);

  if (typeof result === 'string') {
    return notice + result;
  }

  const media = getMCPContentMedia(result);
  return {
    text: notice + (result.text ?? ''),
    media,
    images: result.images ?? media,
  };
}

function formatRecoveryNotice(recovery: RecoveryInfo, logger?: BrowserLogger): string {
  const crashTypeDisplay = recovery.crashType.replace(/_/g, ' ');
  const restoredNote = recovery.urlRestored
    ? `Browser was automatically restored to ${recovery.recoveredUrl}`
    : `Browser was restored but could not navigate back to ${recovery.recoveredUrl}`;

  logger?.info?.('[BrowserTools] Reporting crash recovery to agent', {
    crashType: recovery.crashType,
    recoveredUrl: recovery.recoveredUrl,
    urlRestored: recovery.urlRestored,
  });

  return `[Browser Recovery] ${crashTypeDisplay} detected. ${restoredNote}. You can continue with your task.\n\n`;
}

const MIN_IMAGE_DIMENSION = 512;
const MAX_IMAGE_DIMENSION = 1920;

/**
 * Clamp maxImageDimension to a safe range [512, 1920].
 */
export function clampImageDimension(value: number, logger?: BrowserLogger): number {
  if (value < MIN_IMAGE_DIMENSION || value > MAX_IMAGE_DIMENSION) {
    const clamped = Math.max(MIN_IMAGE_DIMENSION, Math.min(value, MAX_IMAGE_DIMENSION));
    logger?.warn?.('[browser_snapshot] maxImageDimension out of range, clamping', {
      requested: value,
      clamped,
      min: MIN_IMAGE_DIMENSION,
      max: MAX_IMAGE_DIMENSION,
    });
    return clamped;
  }
  return value;
}

/**
 * Build a resolution metadata note for snapshot images.
 * Tells the agent whether the image is downsampled and whether coordinate estimation is safe.
 */
export function buildResolutionNote(
  viewport: { width: number; height: number },
  maxImageDimension: number,
  logger?: BrowserLogger
): string {
  const clamped = clampImageDimension(maxImageDimension, logger);
  const maxDim = Math.max(viewport.width, viewport.height);
  if (maxDim > clamped) {
    return `[Image: downsampled to ~${clamped}px — do NOT estimate coordinates from this image. Use refs or search coordinates instead.]`;
  }
  return `[Image: full resolution ${viewport.width}x${viewport.height} — coordinates can be estimated from this image.]`;
}

/**
 * Validate a URL against an allowed hosts list.
 * Throws if the URL's host is not in the allowed list.
 */
export function ensureAllowedHost(url: string, allowedHosts?: string[]): void {
  if (!allowedHosts || allowedHosts.length === 0) return;

  try {
    const host = new URL(url).host;
    if (!allowedHosts.includes(host)) {
      throw new Error(`Navigation blocked to disallowed host: ${host}`);
    }
  } catch (error) {
    throw error instanceof Error ? error : new Error('Navigation blocked: invalid URL');
  }
}

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

/**
 * RegExp matching only URLs whose host is NOT in `allowedHosts` — the
 * complement of {@link ensureAllowedHost}'s accept set. Used as a
 * `context.route()` pattern so allowed-host traffic never enters the guard
 * handler at all: Playwright's `route.continue()` finalizes a request instead
 * of falling through, so a broad `'**\/*'` guard would swallow other armed
 * context routes (e.g. CF Access scoped-header injection) on the app's own
 * requests. The host boundary is `[/?#]` or end-of-string — deliberately NOT
 * `:` or `@`, so `https://good.com:pass@evil.com/` and userinfo tricks fall
 * through to the guard, whose handler re-validates with the real URL parser.
 */
export function buildOffHostUrlPattern(allowedHosts: string[]): RegExp {
  const hosts = allowedHosts.map(escapeRegExp).join('|');
  return new RegExp(`^https?://(?!(?:${hosts})(?:[/?#]|$))`, 'i');
}

/**
 * Context routes enforcing the allowed-host boundary for NAVIGATIONS at the
 * network layer, so model-authored driver code (`browser_run_code` with `page`
 * in scope) cannot `page.goto()` off-domain past the tool-level
 * `ensureAllowedHost` check on `browser_navigate`. Blocks top-frame document
 * navigations to disallowed hosts with a 403 — including navigations whose
 * frame cannot be classified (the handle reports those as top-frame, failing
 * closed). Subresources and iframe loads pass through via `fallback()` (so this
 * guard can never swallow another owner's route handler), preserving app
 * fidelity.
 *
 * Scope, stated precisely: this closes the NAVIGATION exfil channel (URL-param
 * exfil via `page.goto`/link click/window.location). It does NOT police
 * in-page script traffic — `page.evaluate(() => fetch(...))`, image beacons —
 * which remains governed by the target app's own CSP, the same accepted
 * contract as every other browser-agent surface in this product. Callers whose
 * session must not exist without the boundary should verify the arm succeeded
 * and re-verify after context swaps.
 */
export function buildHostBoundaryContextRoutes(allowedHosts: string[]): ContextRoute[] {
  if (allowedHosts.length === 0) return [];
  const passThrough = (route: ContextRouteHandle): Promise<void> =>
    route.fallback ? route.fallback() : route.continue();
  return [
    {
      urlPattern: buildOffHostUrlPattern(allowedHosts),
      handler: async (route) => {
        if (!route.isMainFrameNavigation()) {
          await passThrough(route);
          return;
        }
        const url = route.request().url();
        try {
          ensureAllowedHost(url, allowedHosts);
        } catch {
          await route.fulfill({
            status: 403,
            headers: { 'content-type': 'text/plain' },
            body:
              `Navigation blocked: ${url} is outside this session's allowed hosts ` +
              `(${allowedHosts.join(', ')}).`,
          });
          return;
        }
        await passThrough(route);
      },
    },
  ];
}
